import { Button, Grid, LinearProgress, Typography } from '@mui/material';
import { DocumentSnapshot, Timestamp, collection, doc, getDoc, getDocs, orderBy, query, setDoc, where } from 'firebase/firestore';
import { PollResponse, ResponseValue, Voter } from '../../types';
import React, { useState } from 'react';

import { db } from '../../firebase';
import { v4 as uuidv4 } from 'uuid';

interface UpmergePollResponsesProps {

}

const UpmergePollResponses: React.FC<UpmergePollResponsesProps> = () => {
  const [upmerging, setUpmerging] = useState(false);
  const [rangeInfo, setRangeInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  const [checkInfo, setCheckInfo] = useState<string | null>(null);
  const [checkProgressCount, setCheckProgressCount] = useState(0);
  const [checkProgress, setCheckProgress] = useState(0);

  const dashboardId = 'SURIGAO DEL NORTE_DISTRICT 1';

  const upmergePollResponse = async (pollResponse: PollResponse, pollResponseDoc: DocumentSnapshot) => {
    const pollResponsesColRef = collection(pollResponseDoc.ref, 'pollResponses');
    const q = query(pollResponsesColRef, orderBy('submittedAt', 'asc'));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.size === 1) {
      console.log('Only one poll response found, skipping...');
      return;
    }

    const pollResponses = querySnapshot.docs.map(doc => doc.data() as PollResponse);
    let responses: { [question: string]: ResponseValue; } | null = null;
    for (const pr of pollResponses) {
      if (!responses) {
        responses = pr.responses;
        continue;
      }

      for (const q in pr.responses) {
        responses[q] = pr.responses[q];
      }
    }

    if (!responses) {
      console.log('No responses found, skipping...');
      return;
    }

    // check if the responses object has the name number of fields in pollResponse.responses
    let allSame = Object.keys(responses).length === Object.keys(pollResponse.responses).length;
    if (allSame) {
      // check if responses is same as pollResponse.responses question by question
      for (const q in responses) {
        if (responses[q] !== pollResponse.responses[q]) {
          allSame = false;
          break;
        }
      }
    }

    if (!allSame) {
      // add a new pollResponse in the collection, set submittedBy to Upmerge
      const newPollResponseRef = doc(db, 'pollResponses', uuidv4());
      await setDoc(newPollResponseRef, {
        ...pollResponse,
        responses,
        id: newPollResponseRef.id,
        submittedBy: 'Upmerge',
        submittedAt: Timestamp.now()
      });
    }
  };

  const checkForUpmerge = async (pollResponse: PollResponse) => {
    if (pollResponse.submittedBy === 'Upmerge') {
      console.log('Poll response submitted by Upmerge, skipping...');
      return;
    }

    if ((pollResponse.submittedBy === 'Dapa Import') ||
      (pollResponse.submittedBy === 'Socorro Import') ||
      (pollResponse.submittedBy === 'Keeta Developer')
    ) {
      console.log('Poll response submitted by Dapa Import, Socorro Import, or Keeta Developer, skipping...');
      return;
    }

    const voterName = pollResponse.responses.VOTER;
    if (!voterName) {
      console.log('Voter name not found in poll response, skipping...');
      return;
    }

    const votersColRef = collection(db, 'voters');
    const q = query(votersColRef, where('name', '==', voterName));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const voter = querySnapshot.docs[0].data() as Voter;

      const latestPollResponsePath = 
        `tabulationsV2/${dashboardId}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}`;

      const latestPollResponseRef = doc(db, latestPollResponsePath);
      const latestPollResponseDoc = await getDoc(latestPollResponseRef);
      if (!latestPollResponseDoc.exists()) {
        console.log('Latest poll response not found, skipping...');
        return;
      }
      const latestPollResponse = latestPollResponseDoc.data() as PollResponse;
      if (latestPollResponse.id === pollResponse.id) {
        upmergePollResponse(pollResponse, latestPollResponseDoc);
      } else {
        console.log('Poll response not latest, skipping...');
      }
    }
  };

  const upMergeOnDateHours = async (startDate: Date, endDate: Date) => {
    const rangeInfo = `Upmerging from ${startDate.toLocaleString()} to ${endDate.toLocaleString()}`;
    console.log(rangeInfo);
    setRangeInfo(rangeInfo);

    // Create Firestore timestamps for query
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    // Reference to pollResponses collection
    const pollResponsesRef = collection(db, 'pollResponses');

    // Create query to filter poll responses within the date range
    const q = query(
      pollResponsesRef, 
      where('submittedAt', '>=', startTimestamp),
      where('submittedAt', '<', endTimestamp),
      where('submittedBy', '!=', 'Upmerge')
    );

    try {
      // Execute the query
      const querySnapshot = await getDocs(q);
      
      // Process retrieved poll responses
      const pollResponses: PollResponse[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PollResponse));

      console.log(`Retrieved ${pollResponses.length} poll responses between ${startDate.toISOString()} and ${endDate.toISOString()}`);

      setCheckProgressCount(pollResponses.length);
      if (pollResponses.length > 0) {
        setCheckProgress(0);
        for (const pollResponse of pollResponses) {
          setCheckInfo(`Checking poll response ${pollResponse.id}...`);
          await checkForUpmerge(pollResponse);
          setCheckProgress(prev => prev + 1);
        }  
      }
    } catch (err) {
      console.error('Error retrieving poll responses:', err);
      throw err;
    }
  }

  const updateLastUpmergeTimestamp = async (timestamp: Date) => {
    try {
      const upmergeDocRef = doc(db, `upmerge/${dashboardId}`);
      await setDoc(upmergeDocRef, {
        last_upmerge_timestamp: Timestamp.fromDate(timestamp)
      }, { merge: true });
    } catch (err) {
      console.error('Error updating last upmerge timestamp:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  const upmergeOnDates = async (startDate: Date, endDate: Date) => {
    // Calculate total hours between startDate and endDate
    const totalHours = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60));
    
    // Create a copy of startDate to iterate
    const currentDate = new Date(startDate);
    let prevDate = new Date(startDate);

    try {
      for (let hour = 0; hour < totalHours; hour++) {
        // Perform upmerge logic for each hour
        console.log(`Processing hour: ${currentDate.toISOString()}`);
        
        // Update progress
        const currentProgress = Math.round((hour / totalHours) * 100);
        setProgress(currentProgress);

        // Increment hour
        currentDate.setHours(currentDate.getHours() + 1);

        await upMergeOnDateHours(prevDate, currentDate);
        await updateLastUpmergeTimestamp(currentDate);

        prevDate = new Date(currentDate);
      }

      // Ensure progress reaches 100%
      setProgress(100);
    } catch (err) {
      console.error('Error during upmerge:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  const handleUpmerge = async () => {
    setUpmerging(true);
    setProgress(0);
    setError(null);

    const upmergeDocRef = doc(db, `upmerge/${dashboardId}`);
    const upmergeDocSnap = await getDoc(upmergeDocRef);
    const upmergeData = upmergeDocSnap.data();

    if (!upmergeData) {
      setError('Upmerge data not found');
      setUpmerging(false);
      return;
    }

    const last_upmerge_timestamp = (upmergeData.last_upmerge_timestamp as Timestamp).toDate();
    console.log('Last upmerge timestamp:', last_upmerge_timestamp);

    // const endDate = new Date("2025-03-16");
    const endDate = new Date();
    endDate.setMinutes(0, 0, 0);
    await upmergeOnDates(last_upmerge_timestamp, endDate);

    setUpmerging(false);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom>
          Upmerge Poll Responses
        </Typography>
      </Grid>
      {error && (
        <Grid item xs={12}>
          <Typography variant="body1" color="error" gutterBottom>
            {error}
          </Typography>
        </Grid>
      )}
      {upmerging && (
        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom>{rangeInfo}</Typography>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="textSecondary" align="center">
            {`${progress}%`}
          </Typography>
        </Grid>
      )}
      {checkProgressCount > 0 && (
        <Grid item xs={12}>
        <Typography variant="body2" gutterBottom>{checkInfo}</Typography>
        <LinearProgress variant="determinate" value={(checkProgress / checkProgressCount) * 100} />
        <Typography variant="body2" color="textSecondary" align="center">
            {`${checkProgress} of ${checkProgressCount}`}
          </Typography>
      </Grid>
      )}
      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={handleUpmerge}
          disabled={upmerging}
        >
          {upmerging ? 'Upmerging...' : 'Upmerge'}
        </Button>
      </Grid>
    </Grid>
  );
};

export default UpmergePollResponses;
